import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Store as StoreIcon,
  Inventory as InventoryIcon,
  TrendingDown as TrendingDownIcon,
  LocalShipping as TransferIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  PointOfSale as POSIcon,
  Warehouse as WarehouseIcon,
  Refresh as RefreshIcon,
  AddCircle as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../apiConfig';

const ERPDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // RTL theme configuration
  const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '"Cairo", "Arial", sans-serif'
    },
    palette: {
      primary: {
        main: '#6366f1',
      },
      secondary: {
        main: '#8b5cf6',
      },
      success: {
        main: '#10b981',
      },
      warning: {
        main: '#f59e0b',
      },
      error: {
        main: '#ef4444',
      },
      info: {
        main: '#3b82f6',
      }
    }
  });

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalStores: 0,
    totalInventoryValue: 0,
    todaySales: 0,
    lowStockItems: 0,
    pendingTransfers: 0,
    totalWarehouses: 0
  });

  // Stores data
  const [stores, setStores] = useState([]);

  // Inventory data
  const [lowStockProducts, setLowStockProducts] = useState([]);

  // Transfers data
  const [transfers, setTransfers] = useState([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [newTransfer, setNewTransfer] = useState({
    fromType: 'store',
    fromId: '',
    toType: 'store',
    toId: '',
    productId: '',
    quantity: 1,
    notes: ''
  });

  // Recent sales
  const [recentSales, setRecentSales] = useState([]);

  // Products data
  const [products, setProducts] = useState([]);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productLocations, setProductLocations] = useState([]);
  const [loadingProductLocations, setLoadingProductLocations] = useState(false);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load stores
      const storesResponse = await fetch(`${API_BASE_URL}/api/erp_stores.php?action=list`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const storesData = await storesResponse.json();

      // Load low stock items
      const lowStockResponse = await fetch(`${API_BASE_URL}/api/erp_inventory.php?action=low_stock&threshold=10`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const lowStockData = await lowStockResponse.json();

      // Load transfers
      const transfersResponse = await fetch(`${API_BASE_URL}/api/erp_inventory.php?action=transfers`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const transfersData = await transfersResponse.json();

      // Load recent sales
      const salesResponse = await fetch(`${API_BASE_URL}/api/erp_pos.php?action=sales&limit=10`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const salesData = await salesResponse.json();

      // Load products summary
      const productsResponse = await fetch(`${API_BASE_URL}/api/admin_products.php?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const productsData = await productsResponse.json();

      // Update states
      if (storesData.success) {
        setStores(storesData.stores || []);
      }

      if (lowStockData.success) {
        setLowStockProducts(lowStockData.products || []);
      }

      if (transfersData.success) {
        setTransfers(transfersData.transfers || []);
      }

      if (salesData.success) {
        setRecentSales(salesData.sales || []);
      }

      if (productsData.success) {
        setProducts(productsData.products || []);
      }

      // Calculate dashboard statistics
      const pendingTransfersCount = (transfersData.transfers || []).filter(t => t.status === 'pending').length;
      const todaySalesTotal = (salesData.sales || [])
        .filter(s => new Date(s.created_at).toDateString() === new Date().toDateString())
        .reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0);

      setDashboardStats({
        totalStores: (storesData.stores || []).length,
        totalInventoryValue: 0, // Will be calculated from inventory
        todaySales: todaySalesTotal,
        lowStockItems: (lowStockData.products || []).length,
        pendingTransfers: pendingTransfersCount,
        totalWarehouses: 3 // From README: Central, South, North
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('فشل في تحميل بيانات لوحة التحكم: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      loadDashboardData();
    }
  }, [loadDashboardData]);

  // Handle refresh
  const handleRefresh = () => {
    loadDashboardData();
    setSuccess('تم تحديث البيانات بنجاح');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Handle transfer creation
  const handleCreateTransfer = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/erp_inventory.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'request_transfer',
          ...newTransfer
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم إنشاء طلب النقل بنجاح');
        setTransferDialogOpen(false);
        loadDashboardData();
        // Reset form
        setNewTransfer({
          fromType: 'store',
          fromId: '',
          toType: 'store',
          toId: '',
          productId: '',
          quantity: 1,
          notes: ''
        });
      } else {
        setError(data.message || 'فشل في إنشاء طلب النقل');
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      setError('حدث خطأ أثناء إنشاء طلب النقل');
    }
  };

  // Load product locations
  const loadProductLocations = async (productId) => {
    try {
      setLoadingProductLocations(true);
      const response = await fetch(`${API_BASE_URL}/api/erp_inventory.php?action=product_locations&product_id=${productId}`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setProductLocations(data.locations || []);
      }
    } catch (error) {
      console.error('Error loading product locations:', error);
    } finally {
      setLoadingProductLocations(false);
    }
  };

  // Handle view product details
  const handleViewProduct = async (product) => {
    setSelectedProduct(product);
    setProductsDialogOpen(true);
    await loadProductLocations(product.id);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-IQ', {
      style: 'decimal',
      minimumFractionDigits: 0
    }).format(amount) + ' د.ع';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'pending': 'warning',
      'in_transit': 'info',
      'completed': 'success',
      'cancelled': 'error',
      'active': 'success',
      'inactive': 'default'
    };
    return colors[status] || 'default';
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'قيد الانتظار',
      'in_transit': 'قيد النقل',
      'completed': 'مكتمل',
      'cancelled': 'ملغي',
      'active': 'نشط',
      'inactive': 'غير نشط'
    };
    return labels[status] || status;
  };

  // Render statistics cards
  const renderStatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  إجمالي المتاجر
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {dashboardStats.totalStores}
                </Typography>
              </Box>
              <StoreIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  مبيعات اليوم
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {formatCurrency(dashboardStats.todaySales)}
                </Typography>
              </Box>
              <MoneyIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          color: 'white',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  منتجات منخفضة المخزون
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {dashboardStats.lowStockItems}
                </Typography>
              </Box>
              <WarningIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  طلبات النقل المعلقة
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {dashboardStats.pendingTransfers}
                </Typography>
              </Box>
              <TransferIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  المستودعات
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {dashboardStats.totalWarehouses}
                </Typography>
              </Box>
              <WarehouseIcon sx={{ fontSize: 48, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          color: '#333',
          height: '100%'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  قيمة المخزون الإجمالية
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                  {formatCurrency(dashboardStats.totalInventoryValue)}
                </Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 48, opacity: 0.6 }} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Render stores overview
  const renderStoresOverview = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <StoreIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            نظرة عامة على المتاجر
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/erp/stores')}
            size="small"
          >
            إدارة المتاجر
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>رمز المتجر</TableCell>
                <TableCell>اسم المتجر</TableCell>
                <TableCell>المدينة</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>مبيعات اليوم</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stores.slice(0, 5).map((store) => (
                <TableRow key={store.id} hover>
                  <TableCell>
                    <Chip label={store.store_code} size="small" color="primary" />
                  </TableCell>
                  <TableCell fontWeight="500">{store.store_name}</TableCell>
                  <TableCell>{store.city}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(store.status)} 
                      size="small" 
                      color={getStatusColor(store.status)}
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(store.today_sales || 0)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="عرض التفاصيل">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => navigate(`/admin/erp/store/${store.id}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {stores.length > 5 && (
          <Box textAlign="center" mt={2}>
            <Button 
              variant="text" 
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/admin/erp/stores')}
            >
              عرض جميع المتاجر ({stores.length})
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render low stock products
  const renderLowStockProducts = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <WarningIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
            منتجات منخفضة المخزون
          </Typography>
          <Button
            variant="outlined"
            startIcon={<InventoryIcon />}
            onClick={() => navigate('/admin/erp/inventory')}
            size="small"
          >
            إدارة المخزون
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>المنتج</TableCell>
                <TableCell>الموقع</TableCell>
                <TableCell>الكمية الحالية</TableCell>
                <TableCell>الحد الأدنى</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lowStockProducts.slice(0, 10).map((product, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {product.product_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {product.sku || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {product.location_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={product.quantity} 
                      size="small" 
                      color="error"
                      icon={<TrendingDownIcon />}
                    />
                  </TableCell>
                  <TableCell>{product.min_stock_level || 10}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<TransferIcon />}
                      onClick={() => {
                        setNewTransfer({
                          ...newTransfer,
                          productId: product.product_id,
                          toId: product.location_id
                        });
                        setTransferDialogOpen(true);
                      }}
                    >
                      طلب نقل
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {lowStockProducts.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              جميع المنتجات ضمن مستويات المخزون الآمنة ✓
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render transfers overview
  const renderTransfersOverview = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <TransferIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            طلبات النقل الأخيرة
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTransferDialogOpen(true)}
            size="small"
          >
            طلب نقل جديد
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>رقم الطلب</TableCell>
                <TableCell>من</TableCell>
                <TableCell>إلى</TableCell>
                <TableCell>المنتج</TableCell>
                <TableCell>الكمية</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>التاريخ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.slice(0, 8).map((transfer) => (
                <TableRow key={transfer.id} hover>
                  <TableCell>
                    <Chip label={`#${transfer.id}`} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{transfer.from_location}</TableCell>
                  <TableCell>{transfer.to_location}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {transfer.product_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={transfer.quantity} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(transfer.status)} 
                      size="small" 
                      color={getStatusColor(transfer.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(transfer.created_at).toLocaleDateString('ar-EG')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {transfers.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              لا توجد طلبات نقل حالياً
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render products management
  const renderProductsManagement = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <InventoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            إدارة المنتجات
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/products')}
              size="small"
            >
              إضافة منتج
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/admin/products')}
              size="small"
            >
              عرض الكل
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>الصورة</TableCell>
                <TableCell>اسم المنتج</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>قسم المستودع</TableCell>
                <TableCell>السعر</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.slice(0, 10).map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Box
                      component="img"
                      src={product.image_url || '/placeholder.png'}
                      alt={product.name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {product.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {product.sku || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={product.storesite || 'غير محدد'} 
                      size="small" 
                      color={product.storesite ? 'primary' : 'default'}
                      variant={product.storesite ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="600" color="success.main">
                      {formatCurrency(product.after_discount || product.price || 0)}
                    </Typography>
                    {product.after_discount && (
                      <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        {formatCurrency(product.price)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={product.status === 'active' ? 'نشط' : 'غير نشط'} 
                      size="small" 
                      color={product.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="عرض المواقع">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleViewProduct(product)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="تعديل">
                      <IconButton 
                        size="small" 
                        color="info"
                        onClick={() => navigate(`/admin/products?edit=${product.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {products.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              لا توجد منتجات
            </Typography>
          </Box>
        )}

        {products.length > 10 && (
          <Box textAlign="center" mt={2}>
            <Button 
              variant="text" 
              endIcon={<ArrowIcon />}
              onClick={() => navigate('/admin/products')}
            >
              عرض جميع المنتجات ({products.length})
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Render recent sales
  const renderRecentSales = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            <POSIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            المبيعات الأخيرة
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => navigate('/admin/pos')}
            size="small"
          >
            نقطة البيع
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>رقم الفاتورة</TableCell>
                <TableCell>المتجر</TableCell>
                <TableCell>المبلغ الإجمالي</TableCell>
                <TableCell>طريقة الدفع</TableCell>
                <TableCell>الوقت</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentSales.slice(0, 8).map((sale) => (
                <TableRow key={sale.id} hover>
                  <TableCell>
                    <Chip label={`INV-${sale.id}`} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>{sale.store_name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="600" color="success.main">
                      {formatCurrency(sale.total_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={sale.payment_method === 'cash' ? 'نقدي' : sale.payment_method === 'card' ? 'بطاقة' : 'مختلط'} 
                      size="small"
                      color={sale.payment_method === 'cash' ? 'success' : 'info'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(sale.created_at).toLocaleTimeString('ar-EG', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {recentSales.length === 0 && (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              لا توجد مبيعات اليوم
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Transfer Dialog
  const renderTransferDialog = () => (
    <Dialog 
      open={transferDialogOpen} 
      onClose={() => setTransferDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <TransferIcon sx={{ mr: 1 }} />
          طلب نقل بضاعة جديد
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="من (النوع)"
                value={newTransfer.fromType}
                onChange={(e) => setNewTransfer({ ...newTransfer, fromType: e.target.value })}
              >
                <MenuItem value="store">متجر</MenuItem>
                <MenuItem value="warehouse">مستودع</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="إلى (النوع)"
                value={newTransfer.toType}
                onChange={(e) => setNewTransfer({ ...newTransfer, toType: e.target.value })}
              >
                <MenuItem value="store">متجر</MenuItem>
                <MenuItem value="warehouse">مستودع</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="رقم المنتج"
                type="number"
                value={newTransfer.productId}
                onChange={(e) => setNewTransfer({ ...newTransfer, productId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الكمية"
                type="number"
                value={newTransfer.quantity}
                onChange={(e) => setNewTransfer({ ...newTransfer, quantity: parseInt(e.target.value) })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={newTransfer.notes}
                onChange={(e) => setNewTransfer({ ...newTransfer, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTransferDialogOpen(false)}>
          إلغاء
        </Button>
        <Button 
          variant="contained" 
          onClick={handleCreateTransfer}
          startIcon={<CheckIcon />}
        >
          إنشاء طلب النقل
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Product Locations Dialog
  const renderProductDialog = () => (
    <Dialog 
      open={productsDialogOpen} 
      onClose={() => {
        setProductsDialogOpen(false);
        setSelectedProduct(null);
        setProductLocations([]);
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <InventoryIcon sx={{ mr: 1 }} />
          مواقع المنتج - {selectedProduct?.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedProduct && (
          <Box sx={{ pt: 2 }}>
            {/* Product Info */}
            <Card sx={{ mb: 3, bgcolor: '#f8fafc' }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Box
                      component="img"
                      src={selectedProduct.image_url || '/placeholder.png'}
                      alt={selectedProduct.name}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 2
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" gutterBottom>
                      {selectedProduct.name}
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Chip label={`SKU: ${selectedProduct.sku || 'N/A'}`} size="small" />
                      <Chip 
                        label={formatCurrency(selectedProduct.after_discount || selectedProduct.price || 0)} 
                        size="small" 
                        color="success"
                      />
                      <Chip 
                        label={selectedProduct.status === 'active' ? 'نشط' : 'غير نشط'} 
                        size="small" 
                        color={selectedProduct.status === 'active' ? 'success' : 'default'}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Locations */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              مواقع المخزون
            </Typography>

            {loadingProductLocations ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>النوع</TableCell>
                      <TableCell>الموقع</TableCell>
                      <TableCell>الكمية المتوفرة</TableCell>
                      <TableCell align="center">الإجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productLocations.map((location, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={location.location_type === 'store' ? 'متجر' : 'مستودع'} 
                            size="small" 
                            color={location.location_type === 'store' ? 'primary' : 'secondary'}
                            icon={location.location_type === 'store' ? <StoreIcon /> : <WarehouseIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {location.location_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {location.store_code || location.warehouse_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={location.quantity} 
                            size="small" 
                            color={location.quantity > 10 ? 'success' : location.quantity > 0 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<TransferIcon />}
                            onClick={() => {
                              setNewTransfer({
                                ...newTransfer,
                                productId: selectedProduct.id,
                                fromType: location.location_type,
                                fromId: location.location_id
                              });
                              setProductsDialogOpen(false);
                              setTransferDialogOpen(true);
                            }}
                          >
                            نقل
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!loadingProductLocations && productLocations.length === 0 && (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="text.secondary">
                  لا توجد مواقع مخزون لهذا المنتج
                </Typography>
              </Box>
            )}

            {/* Total Quantity */}
            {productLocations.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  إجمالي الكمية المتوفرة: {' '}
                  <Chip 
                    label={productLocations.reduce((sum, loc) => sum + parseInt(loc.quantity || 0), 0)} 
                    color="success"
                    size="small"
                  />
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setProductsDialogOpen(false);
          setSelectedProduct(null);
          setProductLocations([]);
        }}>
          إغلاق
        </Button>
        <Button 
          variant="contained" 
          startIcon={<EditIcon />}
          onClick={() => {
            setProductsDialogOpen(false);
            navigate(`/admin/products?edit=${selectedProduct.id}`);
          }}
        >
          تعديل المنتج
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <ThemeProvider theme={rtlTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={rtlTheme}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        direction: 'rtl'
      }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                لوحة تحكم نظام ERP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                إدارة شاملة للمتاجر والمخزون ونقاط البيع
              </Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Tooltip title="تحديث البيانات">
                <IconButton 
                  onClick={handleRefresh}
                  sx={{ 
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<POSIcon />}
                onClick={() => navigate('/admin/pos')}
              >
                نقطة البيع
              </Button>
            </Box>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Statistics Cards */}
          {renderStatsCards()}

          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                إجراءات سريعة
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<StoreIcon />}
                  onClick={() => navigate('/admin/erp/stores')}
                >
                  إدارة المتاجر
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<InventoryIcon />}
                  onClick={() => navigate('/admin/erp/inventory')}
                >
                  إدارة المخزون
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TransferIcon />}
                  onClick={() => setTransferDialogOpen(true)}
                >
                  طلب نقل بضاعة
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<POSIcon />}
                  onClick={() => navigate('/admin/pos')}
                >
                  نقطة البيع
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate('/admin/erp/reports')}
                >
                  التقارير
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Stores Overview */}
          {renderStoresOverview()}

          {/* Products Management */}
          {renderProductsManagement()}

          {/* Two Column Layout */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              {renderLowStockProducts()}
            </Grid>
            <Grid item xs={12} lg={6}>
              {renderTransfersOverview()}
            </Grid>
          </Grid>

          {/* Recent Sales */}
          {renderRecentSales()}

          {/* Transfer Dialog */}
          {renderTransferDialog()}

          {/* Product Locations Dialog */}
          {renderProductDialog()}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ERPDashboard;
